// client/views/BulkEvaluatePage.tsx
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useBulkEvaluateData,
  useBulkActions,
} from '../hooks/useBulkEvaluateData';
import { useFairDecisions, type CreateDecisionPayload } from '../hooks/useFairDecisions';
import { BulkMatrix } from '../components/evaluate/BulkMatrix';
import { ScopeDecisionDialog } from '../components/evaluate/ScopeDecisionDialog';

const ALL_RULES = [
  'F1', 'F2', 'F3', 'F4',
  'A1', 'A1.1', 'A1.2', 'A2',
  'I1', 'I2', 'I3', 'I4',
  'R1', 'R1.1', 'R1.2', 'R1.3',
];

export function BulkEvaluatePage() {
  const { type: typeParam, id: idParam } = useParams<{ type: string; id: string }>();

  const scopeType =
    typeParam === 'catalog' || typeParam === 'schema' ? typeParam : undefined;
  const scopeNaturalKey = idParam ? decodeURIComponent(idParam) : undefined;
  const isGlobalLanding = typeParam === 'global';

  // Always call hooks unconditionally — use sentinel values when params are absent.
  const safeScopeType = scopeType ?? 'catalog';
  const safeScopeKey = scopeNaturalKey ?? '';

  const { data, loading, error, refetch } = useBulkEvaluateData(scopeType, scopeNaturalKey);
  const bulkActions = useBulkActions(safeScopeType, safeScopeKey);
  const { createDecision } = useFairDecisions();
  const navigate = useNavigate();

  const [activeRuleForScopeDecision, setActiveRuleForScopeDecision] = useState<string | null>(
    null,
  );
  const [scopeKind, setScopeKind] = useState<'catalog' | 'schema'>('catalog');
  const [scopeKeyInput, setScopeKeyInput] = useState('');

  // Global landing — shown when the URL is /scope/global/all/evaluate (the
  // top-nav Decisions entry point). The matrix only renders for a specific
  // catalog or schema scope, so we offer a small picker that routes to the
  // right URL once the user types in a scope key.
  if (isGlobalLanding) {
    const submit = (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = scopeKeyInput.trim();
      if (!trimmed) return;
      navigate(`/scope/${scopeKind}/${encodeURIComponent(trimmed)}/evaluate`);
    };
    return (
      <Shell>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ fontSize: '1.4rem', margin: 0 }}>Bulk Decision Matrix</h1>
          <p style={{ color: '#94a3b8', margin: '4px 0 0 0', fontSize: '0.85rem' }}>
            Review FAIR rule decisions across multiple datasets in a chosen scope.
          </p>
        </div>
        <form
          onSubmit={submit}
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-end',
            background: 'rgba(30,41,59,0.6)',
            borderRadius: 8,
            padding: 16,
            marginBottom: 12,
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.8rem' }}>
            <span style={{ color: '#94a3b8' }}>Scope</span>
            <select
              value={scopeKind}
              onChange={(e) => setScopeKind(e.target.value as 'catalog' | 'schema')}
              style={{
                background: '#0f172a',
                color: '#e2e8f0',
                border: '1px solid #334155',
                borderRadius: 4,
                padding: '6px 8px',
              }}
            >
              <option value="catalog">Catalog</option>
              <option value="schema">Schema (catalog.schema)</option>
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.8rem', flex: 1 }}>
            <span style={{ color: '#94a3b8' }}>{scopeKind === 'catalog' ? 'Catalog name' : 'Catalog.schema'}</span>
            <input
              type="text"
              value={scopeKeyInput}
              onChange={(e) => setScopeKeyInput(e.target.value)}
              placeholder={scopeKind === 'catalog' ? 'main' : 'main.silver'}
              style={{
                background: '#0f172a',
                color: '#e2e8f0',
                border: '1px solid #334155',
                borderRadius: 4,
                padding: '6px 8px',
              }}
            />
          </label>
          <button
            type="submit"
            disabled={!scopeKeyInput.trim()}
            style={{
              padding: '6px 16px',
              borderRadius: 6,
              background: scopeKeyInput.trim() ? '#2563eb' : 'rgba(37,99,235,0.4)',
              color: '#ffffff',
              border: 'none',
              cursor: scopeKeyInput.trim() ? 'pointer' : 'not-allowed',
              fontSize: '0.85rem',
            }}
          >
            Open matrix →
          </button>
        </form>
        <p style={{ color: '#64748b', fontSize: '0.75rem', marginTop: 12 }}>
          Tip: open a journey and click the FAIR tab to see decisions for one dataset, or use the Bulk
          Analyze runner in Settings → Integrations to review N tables side-by-side.
        </p>
      </Shell>
    );
  }

  // Guard: invalid scope type
  if (!scopeType) {
    return (
      <Shell>
        <p style={{ color: '#f87171' }}>
          Invalid scope type. Must be <code>catalog</code> or <code>schema</code>.
        </p>
      </Shell>
    );
  }

  if (loading) return <Shell><p style={{ color: '#94a3b8' }}>Loading…</p></Shell>;
  if (error)   return <Shell><p style={{ color: '#f87171' }}>{error}</p></Shell>;
  if (!data)   return null;

  const handleCellClick = (datasetNaturalKey: string, ruleId: string) => {
    const table = data.tables.find((t) => t.dataset_natural_key === datasetNaturalKey);
    if (!table) return;
    navigate(`/analysis/${table.job_id}/evaluate?rule=${ruleId}`);
  };

  /**
   * Compute the most-permissive aggregate machine verdict for a rule across all
   * datasets in the current scope matrix.  Used as the machine_verdict snapshot
   * when creating a scope-level decision so drift detection can function later.
   */
  const computeMachineAggregate = (
    ruleId: string,
  ): 'PASS' | 'FAIL' | 'NOT_APPLICABLE' | null => {
    if (!data) return null;
    const cells = data.matrix.flatMap((row) =>
      row.cells.filter((c) => c.rule === ruleId).map((c) => c.machine_status),
    );
    if (cells.length === 0) return null;
    if (cells.some((s) => s === 'PASS')) return 'PASS';
    if (cells.some((s) => s === 'FAIL')) return 'FAIL';
    return 'NOT_APPLICABLE';
  };

  const handleColumnHeaderClick = (ruleId: string) => {
    setActiveRuleForScopeDecision(ruleId);
  };

  const handleScopeSubmit = async (payload: CreateDecisionPayload) => {
    await createDecision(payload);
    setActiveRuleForScopeDecision(null);
    await refetch();
  };

  const handleAcceptAll = async () => {
    if (!scopeNaturalKey) return;
    if (
      !window.confirm(
        `Accept all 16 rules at ${scopeType} scope (${scopeNaturalKey})? ` +
        `This will fan out to ${data.tables.length} table(s).`,
      )
    ) {
      return;
    }
    await bulkActions.bulkAccept(ALL_RULES, 'reviewed in batch');
    await refetch();
  };

  const handleReconfirmStale = async () => {
    if (!scopeNaturalKey) return;
    if (
      !window.confirm(
        `Reconfirm all stale decisions at ${scopeType} scope (${scopeNaturalKey})? ` +
        `This refreshes the engine version stamp on each existing decision; verdicts and rationale are preserved.`,
      )
    ) {
      return;
    }
    await bulkActions.bulkReconfirmStale();
    await refetch();
  };

  const hasStaleCells = data.matrix.some((row) => row.cells.some((c) => c.stale));

  return (
    <Shell>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: '1.4rem', margin: 0 }}>Bulk Evaluate</h1>
        <p style={{ color: '#94a3b8', margin: '4px 0 0 0', fontSize: '0.85rem' }}>
          {data.scope.type} ·{' '}
          <span
            style={{ color: '#a5b4fc', fontFamily: 'JetBrains Mono, Consolas, monospace' }}
          >
            {data.scope.natural_key}
          </span>
          {' · '}{data.tables.length} table{data.tables.length === 1 ? '' : 's'}
          {' · Engine '}{data.engine_version}
        </p>
      </div>

      <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={handleAcceptAll}
          style={{
            padding: '6px 12px',
            borderRadius: 6,
            background: 'rgba(34,197,94,0.15)',
            color: '#4ade80',
            border: '1px solid rgba(34,197,94,0.3)',
            cursor: 'pointer',
            fontSize: '0.8rem',
          }}
        >
          ✓ Accept all rules at this scope
        </button>
        {hasStaleCells && (
          <button
            type="button"
            onClick={handleReconfirmStale}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              background: 'rgba(245,158,11,0.15)',
              color: '#fbbf24',
              border: '1px solid rgba(245,158,11,0.3)',
              cursor: 'pointer',
              fontSize: '0.8rem',
            }}
          >
            ⏵ Reconfirm stale decisions
          </button>
        )}
      </div>

      {data.scope_decisions.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <span
            style={{
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              color: '#94a3b8',
              letterSpacing: '0.05em',
            }}
          >
            Scope decisions:
          </span>
          <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            {data.scope_decisions.map((d) => (
              <span
                key={d.id}
                style={{
                  padding: '3px 8px',
                  borderRadius: 9999,
                  background: 'rgba(168,85,247,0.12)',
                  color: '#c084fc',
                  fontSize: '0.7rem',
                  border: '1px solid rgba(168,85,247,0.3)',
                }}
              >
                {d.rule_id} · {d.action} · {d.asserted_status}
              </span>
            ))}
          </div>
        </div>
      )}

      <BulkMatrix
        matrix={data.matrix}
        onCellClick={handleCellClick}
        onColumnHeaderClick={handleColumnHeaderClick}
      />

      {activeRuleForScopeDecision && scopeNaturalKey && (
        <ScopeDecisionDialog
          ruleId={activeRuleForScopeDecision}
          scopeType={scopeType}
          scopeNaturalKey={scopeNaturalKey}
          machineVerdictAtDecisionTime={computeMachineAggregate(activeRuleForScopeDecision)}
          onSubmit={handleScopeSubmit}
          onCancel={() => setActiveRuleForScopeDecision(null)}
        />
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: 24,
        maxWidth: 1400,
        margin: '0 auto',
        color: '#e2e8f0',
        background: '#0f172a',
        minHeight: '100vh',
      }}
    >
      {children}
    </div>
  );
}
